import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import { useAuth } from './AuthContext';
import { fetchAllPages } from '../utils/fetchAllPages';

const CustomerContext = createContext();

export const useCustomers = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomers must be used within a CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  // Load customers when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadCustomers();
    } else {
      // Clear customers when not authenticated
      setCustomers([]);
    }
  }, [isAuthenticated]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const all = await fetchAllPages(
        (page, limit) => apiService.getCustomers({ page, limit }),
        'customers',
        100
      );
      setCustomers(all);
    } catch (error) {

      // Set empty array if API fails
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const pickCustomerPayload = (customerData = {}) => {
    const allowed = [
      'name',
      'contactPerson',
      'email',
      'phone',
      'address',
      'city',
      'country',
      'tin',
      'ghanaCard',
      'customerType',
      'status'
    ];
    const payload = {};
    allowed.forEach((key) => {
      if (customerData[key] !== undefined) {
        payload[key] = customerData[key];
      }
    });
    if (payload.email !== undefined) {
      payload.email =
        typeof payload.email === 'string' && payload.email.trim()
          ? payload.email.trim()
          : null;
    }
    if (payload.tin !== undefined) {
      payload.tin =
        typeof payload.tin === 'string' && payload.tin.trim()
          ? payload.tin.trim()
          : null;
    }
    if (payload.phone !== undefined && typeof payload.phone === 'string') {
      payload.phone = payload.phone.trim();
    }
    return payload;
  };

  // Add new customer
  const addCustomer = async (customerData) => {
    try {
      const payload = pickCustomerPayload(customerData);
      const response = await apiService.createCustomer(payload);

      const newCustomer = response.customer;

      setCustomers(prev => [...prev, newCustomer]);

      return newCustomer;
    } catch (error) {

      throw error;
    }
  };

  // Update existing customer
  const updateCustomer = async (id, customerData) => {
    try {
      const payload = pickCustomerPayload(customerData);
      const response = await apiService.updateCustomer(id, payload);
      const updatedCustomer = response.customer;
      setCustomers(prev => 
        prev.map(customer => 
          customer.id === id ? updatedCustomer : customer
        )
      );
      return updatedCustomer;
    } catch (error) {

      throw error;
    }
  };

  // Delete customer
  const deleteCustomer = async (id) => {
    try {
      await apiService.deleteCustomer(id);
      setCustomers(prev => prev.filter(customer => customer.id !== id));
    } catch (error) {

      throw error;
    }
  };

  // Get customer by ID
  const getCustomerById = (id) => {
    return customers.find(customer => customer.id === id);
  };

  // Search customers
  const searchCustomers = (searchTerm) => {
    const term = searchTerm.toLowerCase();
    return customers.filter(customer =>
      customer.name?.toLowerCase().includes(term) ||
      customer.email?.toLowerCase().includes(term) ||
      customer.phone?.includes(term)
    );
  };

  const value = {
    customers,
    loading,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    getCustomerById,
    searchCustomers
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerContext;

